import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { AnimatePresence } from 'framer-motion';

import { UbotsChatButton } from '../components';
import { useAuth } from '../hooks/useAuth';
import {
  CadastrarInstrucoes,
  CadastrarManual,
  CadastrarMensagemCritica,
  CadastrarPerfis,
  CadastrarPerguntas,
  CadastrarTelas,
  Dashboard,
  DashboardConvenios,
  DashboardPagamentos,
  EditarInstrucoes,
  EditarManual,
  EditarMensagemCritica,
  EditarPerfis,
  EditarPerguntas,
  EditarTelas,
  Home,
  ListarInstrucoes,
  ListarManual,
  ListarMensagemCritica,
  ListarPerfis,
  ListarPerguntas,
  ListarTelas,
  Login,
  LoginExterno,
  MotorCobranca,
  MotorConvenios,
  MotorPagamentos,
  Session404,
  SessionExpired,
  SessionUnauthorized,
} from '../pages';
import PrivateRoute from './Route';

const MOTOR_ROUTES = ['/motor', '/motor-pagamentos', '/motor-convenios'];

const Routes: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const isMotorRoute = MOTOR_ROUTES.includes(location.pathname);

  return (
    <>
      {isMotorRoute && <UbotsChatButton />}
      <AnimatePresence exitBeforeEnter>
        <Switch location={location} key={location.pathname}>
          <Route exact path="/">
            {isAuthenticated() ? (
              <Redirect to={RoutesPaths.home} />
            ) : (
              <Redirect to={RoutesPaths.login} />
            )}
          </Route>
          <Route path={RoutesPaths.login} exact component={Login} />
          <Route
            path={RoutesPaths.loginExterno}
            exact
            component={LoginExterno}
          />
          <Route path={'/cooperado'} component={LoginExterno} />
          <PrivateRoute path={RoutesPaths.home} exact component={Home} />
          <PrivateRoute
            path={RoutesPaths.motorCobranca}
            exact
            component={MotorCobranca}
          />
          <PrivateRoute
            path={RoutesPaths.motorPagamentos}
            exact
            component={MotorPagamentos}
          />
          <PrivateRoute
            path={RoutesPaths.motorConvenios}
            exact
            component={MotorConvenios}
          />
          <PrivateRoute
            path={RoutesPaths.registerFAQ}
            exact
            component={CadastrarPerguntas}
          />
          <PrivateRoute
            path={RoutesPaths.listFAQ}
            exact
            component={ListarPerguntas}
          />
          <PrivateRoute
            path={`${RoutesPaths.editFAQ}:id`}
            exact
            component={EditarPerguntas}
          />
          <PrivateRoute
            path={RoutesPaths.dashboardCobranca}
            exact
            component={Dashboard}
          />
          <PrivateRoute
            path={RoutesPaths.dashboardPagamentos}
            exact
            component={DashboardPagamentos}
          />
          <PrivateRoute
            path={RoutesPaths.dashboardConvenios}
            exact
            component={DashboardConvenios}
          />
          <PrivateRoute
            path={RoutesPaths.registerCriticalMessage}
            exact
            component={CadastrarMensagemCritica}
          />
          <PrivateRoute
            path={RoutesPaths.listCriticalMessage}
            exact
            component={ListarMensagemCritica}
          />
          <PrivateRoute
            path={`${RoutesPaths.editCriticalMessage}:id`}
            exact
            component={EditarMensagemCritica}
          />

          <PrivateRoute
            path={RoutesPaths.registerScreen}
            exact
            component={CadastrarTelas}
          />
          <PrivateRoute
            path={RoutesPaths.listScreen}
            exact
            component={ListarTelas}
          />
          <PrivateRoute
            path={`${RoutesPaths.editScreen}:id`}
            exact
            component={EditarTelas}
          />

          <PrivateRoute
            path={RoutesPaths.registerPerfil}
            exact
            component={CadastrarPerfis}
          />
          <PrivateRoute
            path={RoutesPaths.listPerfil}
            exact
            component={ListarPerfis}
          />
          <PrivateRoute
            path={`${RoutesPaths.editPerfil}:id`}
            exact
            component={EditarPerfis}
          />

          <PrivateRoute
            path={RoutesPaths.registerInstruction}
            exact
            component={CadastrarInstrucoes}
          />
          <PrivateRoute
            path={RoutesPaths.listInstruction}
            exact
            component={ListarInstrucoes}
          />
          <PrivateRoute
            path={`${RoutesPaths.editInstruction}:id`}
            exact
            component={EditarInstrucoes}
          />

          <PrivateRoute
            path={RoutesPaths.registerManual}
            exact
            component={CadastrarManual}
          />
          <PrivateRoute
            path={RoutesPaths.listManual}
            exact
            component={ListarManual}
          />
          <PrivateRoute
            path={`${RoutesPaths.editManual}:id`}
            exact
            component={EditarManual}
          />
          <Route path={RoutesPaths.expired} exact component={SessionExpired} />
          <Route
            path={RoutesPaths.unauthorized}
            component={SessionUnauthorized}
          />
          <Route path={RoutesPaths.notFound} component={Session404} />
        </Switch>
      </AnimatePresence>
    </>
  );
};

class RoutesPaths {
  static dashboardCobranca = '/dashboard';
  static dashboardPagamentos = '/dashboard-pagamentos';
  static dashboardConvenios = '/dashboard-convenios';
  static login = '/login';
  static loginExterno = '/login-externo';
  static home = '/home';
  static expired = '/acesso/expirado';
  static unauthorized = '/acesso/negado';
  static motorCobranca = '/motor';
  static motorPagamentos = '/motor-pagamentos';
  static motorConvenios = '/motor-convenios';
  static notFound = '**';

  static listManual = '/manual/listar';
  static editManual = '/manual/editar/';
  static registerManual = '/manual/cadastrar';

  static listInstruction = '/instrucao/listar';
  static editInstruction = '/instrucao/editar/';
  static registerInstruction = '/instrucao/cadastrar';

  static listFAQ = '/pergunta/listar';
  static editFAQ = '/pergunta/editar/';
  static registerFAQ = '/pergunta/cadastrar';

  static listPerfil = '/perfil/listar';
  static editPerfil = '/perfil/editar/';
  static registerPerfil = '/perfil/cadastrar';

  static listScreen = '/telas/listar';
  static editScreen = '/telas/editar/';
  static registerScreen = '/telas/cadastrar';

  static listCriticalMessage = '/mensagens-criticas/listar';
  static editCriticalMessage = '/mensagens-criticas/editar/';
  static registerCriticalMessage = '/mensagens-criticas/cadastrar';
}
export { RoutesPaths };

export default Routes;
